import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@/features/auth/AuthContext'
import { createCompany, fetchCompanies } from '@/features/companies/api'
import LoadingState from '@/components/common/LoadingState'
import EmptyState from '@/components/common/EmptyState'
import ErrorState from '@/components/common/ErrorState'

/**
 * Ports includes/client_management/company_card.html (list) and
 * company_add.html (create form, gated by clients.create — same permission
 * the legacy create_company() route already enforces).
 */
export default function CompaniesPage() {
  const { hasPermission } = useAuth()
  const canCreate = hasPermission('clients.create')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [newCompanyName, setNewCompanyName] = useState('')
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [search, setSearch] = useState('')

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['companies'],
    queryFn: fetchCompanies,
  })

  const filtered = (data ?? []).filter((company) => {
    if (!search) return true
    const q = search.toLowerCase()
    return company.company_name.toLowerCase().includes(q) || company.company_id.toLowerCase().includes(q)
  })

  const createMutation = useMutation({
    mutationFn: () => createCompany(newCompanyName.trim()),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['companies'] })
      setNewCompanyName('')
      setShowCreateForm(false)
      navigate(`/companies/${result.company_id}`)
    },
  })

  return (
    // Same shrink-0-header / flex-1-scroller split as DashboardPage: fills
    // AppLayout's <main> exactly (h-full) so the table below is the ONLY
    // thing that scrolls, with the title/create-form staying pinned.
    <div className="flex h-full flex-col gap-4">
      <div className="shrink-0">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-bold text-ink">Companies</h1>
          {canCreate && (
            <button
              type="button"
              onClick={() => setShowCreateForm((v) => !v)}
              className="rounded-full bg-brand px-5 py-2 text-sm font-semibold text-white hover:opacity-90"
            >
              {showCreateForm ? 'Cancel' : 'Add Company'}
            </button>
          )}
        </div>

        {showCreateForm && (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (newCompanyName.trim()) createMutation.mutate()
            }}
            className="mb-4 flex items-end gap-2 rounded-2xl border border-nav-line bg-card p-3"
          >
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-nav-muted">Company name</label>
              <input
                value={newCompanyName}
                onChange={(e) => setNewCompanyName(e.target.value)}
                className="rounded-xl border border-nav-line bg-input px-3.5 py-2.5 text-sm text-text-primary focus:border-brand focus:outline-none"
                autoFocus
              />
            </div>
            <button
              type="submit"
              disabled={createMutation.isPending || !newCompanyName.trim()}
              className="rounded-full bg-brand px-5 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
            >
              {createMutation.isPending ? 'Creating…' : 'Create'}
            </button>
            {createMutation.isError && <p className="text-xs text-error">Failed to create company.</p>}
          </form>
        )}

        {data && data.length > 0 && (
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…"
            className="mb-4 w-full max-w-sm rounded-full border border-transparent bg-shell px-[18px] py-2.5 text-sm text-text-primary focus:border-brand focus:outline-none"
          />
        )}

        {isLoading && <LoadingState label="Loading companies…" />}

        {isError && (
          <ErrorState
            message={error instanceof Error ? error.message : 'Failed to load companies.'}
            onRetry={() => refetch()}
          />
        )}

        {data && data.length === 0 && <EmptyState message="No companies yet." />}

        {data && data.length > 0 && filtered.length === 0 && <EmptyState message="No companies match." />}
      </div>

      {filtered.length > 0 && (
        <div className="min-h-0 flex-1 overflow-x-auto overflow-y-auto rounded-2xl border border-nav-line bg-card">
          <table className="w-full text-[13.5px]">
            <thead className="sticky top-0 z-10 bg-card">
              <tr className="border-b border-nav-line text-left text-xs text-nav-muted">
                <th className="px-4 py-3.5 font-semibold">Company Name</th>
                <th className="px-4 py-3.5 font-semibold">Users</th>
                <th className="px-4 py-3.5 font-semibold">Controllers</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((company) => (
                <tr key={company.company_id} className="border-b border-nav-line last:border-0 hover:bg-nav-hover">
                  <td className="px-4 py-3.5">
                    <Link to={`/companies/${company.company_id}`} className="text-accent hover:underline">
                      {company.company_name}
                    </Link>
                  </td>
                  <td className="px-4 py-3.5 text-ink">{company.user_count}</td>
                  <td className="px-4 py-3.5 text-ink">{company.controller_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
